import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import db from '../services/db';
import { formatInlineToHtml } from '../utils/inlineMarkdown';
import RichTextSlideRenderer from './RichTextSlideRenderer';
import ExerciseLead, { EXERCISE_CARD_PAD } from './ExerciseLead';
import { describeVoiceRecording, markVoiceRecordingMistakes } from '../services/voiceRecordAi';
import './ExerciseGameUI.css';

function pickAudioMime() {
    const candidates = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/mp4',
        'audio/ogg;codecs=opus',
        'audio/ogg',
    ];
    if (typeof MediaRecorder === 'undefined') return '';
    return candidates.find((t) => MediaRecorder.isTypeSupported?.(t)) || '';
}

function extFromMime(mime) {
    const m = (mime || '').toLowerCase();
    if (m.includes('webm')) return 'webm';
    if (m.includes('ogg')) return 'ogg';
    if (m.includes('mp4') || m.includes('m4a')) return 'm4a';
    return 'webm';
}

function formatMmSs(totalSec) {
    const s = Math.max(0, Math.floor(Number(totalSec) || 0));
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
}

function resolveVoiceSettings(exercise) {
    const a = exercise?.assessment || {};
    const mode = ['countdown', 'stopwatch', 'none'].includes(a.voiceTimerMode)
        ? a.voiceTimerMode
        : 'none';
    const limitRaw = Number(a.voiceTimeLimitSec);
    const limitSec = Number.isFinite(limitRaw) && limitRaw > 0 ? Math.round(limitRaw) : null;
    const attempts = a.voiceAttempts === 'one' ? 'one' : 'multiple';
    return { mode, limitSec, attempts };
}

function isAlreadyDone(assignment) {
    const st = String(assignment?.status || '').toLowerCase();
    return st === 'completed' || st === 'checked';
}

/** Match shared Interactive card shell (negative-margin instruction/notes need overflow). */
const CARD = {
    background: '#ffffff',
    borderRadius: 20,
    padding: EXERCISE_CARD_PAD,
    border: '1.5px solid #e2e8f0',
    maxWidth: '100%',
    overflow: 'hidden',
    width: '100%',
    boxSizing: 'border-box',
};

/**
 * Voice Recording exercise: prompt text + mic recorder (countdown / stopwatch) + teacher AI tools.
 */
export default function VoiceRecordInteractive({
    exercise,
    assignment,
    preview,
    onComplete,
    reviewMode,
    onUpdate,
    onUpdateAssignment,
    readOnly,
    isOnlineLesson,
    user,
    isTeacher,
    practiceMode,
}) {
    const settings = useMemo(() => resolveVoiceSettings(exercise), [exercise]);
    const alreadyDone = isAlreadyDone(assignment) && !reviewMode;
    const locked = !!(readOnly || reviewMode || alreadyDone || preview);
    const userId = user?.id || assignment?.student_id || assignment?.studentId;

    const initialMedia = assignment?.answers?.audioUrl
        ? { url: assignment.answers.audioUrl, path: assignment.answers.audioPath || null }
        : null;

    const [media, setMedia] = useState(initialMedia);
    const [completed, setCompleted] = useState(locked && !!initialMedia);
    const [recording, setRecording] = useState(false);
    const [elapsed, setElapsed] = useState(0);
    const [error, setError] = useState('');
    const [uploading, setUploading] = useState(false);
    const [transcript, setTranscript] = useState(assignment?.answers?.transcript || '');
    const [marked, setMarked] = useState(assignment?.answers?.transcriptMarked || '');
    const [aiBusy, setAiBusy] = useState('');
    const [playUrl, setPlayUrl] = useState(null);

    const mediaRecorderRef = useRef(null);
    const streamRef = useRef(null);
    const chunksRef = useRef([]);
    const elapsedRef = useRef(0);

    useEffect(() => {
        elapsedRef.current = elapsed;
    }, [elapsed]);

    useEffect(() => {
        if (assignment?.answers?.audioUrl) {
            setMedia({ url: assignment.answers.audioUrl, path: assignment.answers.audioPath || null });
        }
        if (assignment?.answers?.transcript) setTranscript(assignment.answers.transcript);
        if (assignment?.answers?.transcriptMarked) setMarked(assignment.answers.transcriptMarked);
    }, [assignment?.id]);

    useEffect(() => {
        let cancelled = false;
        let blobUrl = null;
        (async () => {
            if (!media?.url && !media?.path) {
                setPlayUrl(null);
                return;
            }
            try {
                const src = media.path || media.url;
                blobUrl = await db.downloadWritingCheckerMediaAsBlobUrl(src);
                if (!cancelled) setPlayUrl(blobUrl);
            } catch {
                try {
                    const fresh = await db.getFreshWritingCheckerMediaUrl(media.url);
                    if (!cancelled) setPlayUrl(fresh);
                } catch {
                    if (!cancelled) setPlayUrl(media.url);
                }
            }
        })();
        return () => {
            cancelled = true;
            if (blobUrl) URL.revokeObjectURL(blobUrl);
        };
    }, [media?.url, media?.path]);

    const persistAnswers = useCallback((patch, status) => {
        if (isOnlineLesson) return;
        const nextAnswers = {
            ...(assignment?.answers || {}),
            audioUrl: media?.url || null,
            audioPath: media?.path || null,
            transcript,
            transcriptMarked: marked,
            ...patch,
        };
        const nextStatus = status || (completed ? 'completed' : (assignment?.status || 'working'));
        // Teacher review (incl. block sub-exercises) only wires onUpdateAssignment — not onUpdate.
        if (typeof onUpdateAssignment === 'function' && assignment?.id) {
            onUpdateAssignment(assignment.id, {
                answers: nextAnswers,
                status: nextStatus,
            });
            return;
        }
        if (typeof onUpdate === 'function') {
            onUpdate({
                answers: nextAnswers,
                status: nextStatus,
            });
        }
    }, [
        onUpdate,
        onUpdateAssignment,
        isOnlineLesson,
        assignment?.id,
        assignment?.answers,
        assignment?.status,
        media,
        transcript,
        marked,
        completed,
    ]);

    const stopStream = () => {
        try {
            streamRef.current?.getTracks?.().forEach((tr) => tr.stop());
        } catch { /* ignore */ }
        streamRef.current = null;
    };

    const finishRecordingUpload = async (blob, mimeType) => {
        if (!blob?.size) return;
        if (!userId) {
            setError('Sign in to upload recordings.');
            return;
        }
        if (media?.path) {
            try { await db.deleteWritingCheckerMedia(media.path); } catch { /* ignore */ }
        }
        setUploading(true);
        try {
            const ext = extFromMime(mimeType || blob.type);
            const file = new File([blob], `voice.${ext}`, { type: blob.type || mimeType || 'audio/webm' });
            const uploaded = await db.uploadWritingCheckerMedia(userId, file);
            if (!uploaded?.url) throw new Error('Upload did not return a URL.');
            const next = { url: uploaded.url, path: uploaded.path };
            setMedia(next);
            const duration = elapsedRef.current;
            setTranscript('');
            setMarked('');
            persistAnswers({
                audioUrl: next.url,
                audioPath: next.path,
                recordingDurationSec: duration,
                transcript: '',
                transcriptMarked: '',
            }, 'working');
        } catch (e) {
            setError(e?.message || 'Upload failed');
        } finally {
            setUploading(false);
        }
    };

    const stopRecording = useCallback(() => {
        try {
            if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
                mediaRecorderRef.current.stop();
            }
        } catch { /* ignore */ }
        setRecording(false);
    }, []);

    useEffect(() => {
        if (!recording) return undefined;
        const t = setInterval(() => {
            setElapsed((s) => {
                const next = s + 1;
                if (settings.mode === 'countdown' && settings.limitSec && next >= settings.limitSec) {
                    clearInterval(t);
                    queueMicrotask(() => stopRecording());
                } else if (settings.mode === 'stopwatch' && settings.limitSec && next >= settings.limitSec) {
                    clearInterval(t);
                    queueMicrotask(() => stopRecording());
                }
                return next;
            });
        }, 1000);
        return () => clearInterval(t);
    }, [recording, settings.mode, settings.limitSec, stopRecording]);

    const startRecording = async () => {
        if (locked || uploading) return;
        setError('');
        setElapsed(0);
        chunksRef.current = [];
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;
            const mimeType = pickAudioMime();
            const mr = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
            mediaRecorderRef.current = mr;
            mr.ondataavailable = (e) => {
                if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
            };
            mr.onstop = async () => {
                stopStream();
                const blob = new Blob(chunksRef.current, { type: mr.mimeType || 'audio/webm' });
                chunksRef.current = [];
                await finishRecordingUpload(blob, mr.mimeType);
            };
            mr.start(250);
            setRecording(true);
        } catch (e) {
            stopStream();
            setError(e?.message || 'Microphone access denied');
        }
    };

    const canReRecord = !locked && settings.attempts === 'multiple' && !!media?.url && !recording;
    const canStartFirst = !locked && !media?.url && !recording;
    const displayTime = settings.mode === 'countdown' && settings.limitSec
        ? formatMmSs(Math.max(0, settings.limitSec - elapsed))
        : formatMmSs(elapsed);

    const promptHtml = useMemo(() => {
        const raw = String(exercise?.body || '');
        if (!raw.trim()) return '';
        return raw
            .split(/\n/)
            .map((line) => `<div>${formatInlineToHtml(line, 'vr') || '<br>'}</div>`)
            .join('');
    }, [exercise?.body]);

    const handleComplete = () => {
        if (!media?.url) {
            setError('Record your answer before completing.');
            return;
        }
        setCompleted(true);
        if (onComplete) {
            onComplete({
                status: 'completed',
                score: 1,
                total: 1,
                answers: {
                    audioUrl: media.url,
                    audioPath: media.path,
                    transcript,
                    transcriptMarked: marked,
                },
            });
        } else if (!isOnlineLesson) {
            const answers = {
                ...(assignment?.answers || {}),
                audioUrl: media.url,
                audioPath: media.path,
                transcript,
                transcriptMarked: marked,
            };
            if (typeof onUpdateAssignment === 'function' && assignment?.id) {
                onUpdateAssignment(assignment.id, { status: 'completed', score: 1, total: 1, answers });
            } else if (typeof onUpdate === 'function') {
                onUpdate({ status: 'completed', score: 1, total: 1, answers });
            }
        }
    };

    const handleDescribe = async () => {
        if (!media?.url) return;
        setAiBusy('describe');
        setError('');
        try {
            const fresh = await db.getFreshWritingCheckerMediaUrl(media.url).catch(() => media.url);
            const { transcript: t } = await describeVoiceRecording(fresh);
            setTranscript(t);
            persistAnswers({ transcript: t, audioUrl: media.url, audioPath: media.path }, assignment?.status);
        } catch (e) {
            setError(e?.message || 'Describe failed');
        } finally {
            setAiBusy('');
        }
    };

    const handleMarkMistakes = async () => {
        const source = transcript.trim();
        if (!source) {
            setError('Run AI Describe first (or paste a transcript).');
            return;
        }
        setAiBusy('mark');
        setError('');
        try {
            const m = await markVoiceRecordingMistakes(source);
            setMarked(m);
            persistAnswers({ transcriptMarked: m, transcript: source }, assignment?.status);
        } catch (e) {
            setError(e?.message || 'Mark mistakes failed');
        } finally {
            setAiBusy('');
        }
    };

    const showTeacherTools = isTeacher || reviewMode;

    return (
        <div className="exercise-card-shell" style={CARD}>
            <ExerciseLead exercise={exercise} />

            <RichTextSlideRenderer
                content={promptHtml}
                style={{ lineHeight: 1.75, fontSize: '1.05rem', color: '#1e293b', marginBottom: '1.25rem' }}
            />

            <div style={{
                border: '1.5px solid #e2e8f0',
                borderRadius: 16,
                padding: '1rem 1.1rem',
                background: '#f8fafc',
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
            }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                    <div style={{ fontWeight: 900, color: '#0f172a', fontSize: '0.95rem' }}>🎙 Voice answer</div>
                    {settings.mode !== 'none' && (
                        <div style={{
                            fontVariantNumeric: 'tabular-nums',
                            fontWeight: 900,
                            fontSize: '1.35rem',
                            color: recording && settings.mode === 'countdown' && settings.limitSec && (settings.limitSec - elapsed) <= 10
                                ? '#dc2626'
                                : '#4361ee',
                        }}>
                            {settings.mode === 'countdown' ? '⏱ ' : '⏲ '}{displayTime}
                            {settings.mode === 'stopwatch' && settings.limitSec ? (
                                <span style={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: 700, marginLeft: 8 }}>
                                    / {formatMmSs(settings.limitSec)}
                                </span>
                            ) : null}
                        </div>
                    )}
                </div>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                    {!recording && (canStartFirst || canReRecord) && (
                        <button
                            type="button"
                            onClick={startRecording}
                            disabled={uploading}
                            style={{
                                border: 'none', borderRadius: 12, padding: '10px 16px',
                                background: '#4361ee', color: 'white', fontWeight: 800,
                                cursor: uploading ? 'wait' : 'pointer', fontSize: '0.9rem',
                            }}
                        >
                            {media?.url ? '🔄 Re-record' : '⏺ Record'}
                        </button>
                    )}
                    {recording && (
                        <button
                            type="button"
                            onClick={stopRecording}
                            style={{
                                border: 'none', borderRadius: 12, padding: '10px 16px',
                                background: '#ef4444', color: 'white', fontWeight: 800,
                                cursor: 'pointer', fontSize: '0.9rem',
                            }}
                        >
                            ⏹ Stop
                        </button>
                    )}
                    {uploading && <span style={{ color: '#64748b', fontWeight: 700, fontSize: '0.85rem' }}>Uploading…</span>}
                    {settings.attempts === 'one' && media?.url && !locked && (
                        <span style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 700 }}>1 attempt — recording locked</span>
                    )}
                </div>

                {playUrl && (
                    <audio controls src={playUrl} style={{ width: '100%' }} />
                )}

                {error && (
                    <div style={{ color: '#b91c1c', fontWeight: 700, fontSize: '0.82rem' }}>{error}</div>
                )}
            </div>

            {!preview && !isOnlineLesson && (
                <div className="exercise-action-bar" style={{ marginTop: '1.1rem' }}>
                    {!readOnly && !reviewMode && (
                        <button
                            type="button"
                            className={(alreadyDone || completed)
                                ? 'exercise-game-btn exercise-game-btn--marked-done'
                                : 'exercise-game-btn exercise-game-btn--complete'}
                            onClick={(alreadyDone || completed) ? undefined : handleComplete}
                            disabled={(alreadyDone || completed) || !media?.url || recording || uploading}
                            style={(alreadyDone || completed) ? undefined : {
                                opacity: (!media?.url || recording || uploading) ? 0.55 : 1,
                                cursor: (!media?.url || recording || uploading) ? 'not-allowed' : 'pointer',
                            }}
                        >
                            {(alreadyDone || completed) ? 'Done ✅' : 'Mark Done'}
                        </button>
                    )}
                    {(assignment?.status || completed) && (
                        <div className="exercise-status-pill">
                            Status: {assignment?.status || (completed ? 'completed' : 'working')}
                        </div>
                    )}
                </div>
            )}

            {isOnlineLesson && isTeacher && (
                <div style={{ marginTop: 12, fontSize: '0.8rem', color: '#64748b', fontWeight: 700 }}>
                    Voice recording is for homework / individual practice — use Assign, not live Shared mode.
                </div>
            )}

            {showTeacherTools && (
                <div style={{
                    marginTop: '1.25rem',
                    padding: '1rem',
                    borderRadius: 14,
                    border: '1.5px solid #fde68a',
                    background: '#fffbeb',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 10,
                }}>
                    <div style={{ fontWeight: 900, color: '#92400e', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        Teacher — AI check
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        <button
                            type="button"
                            disabled={!media?.url || !!aiBusy}
                            onClick={handleDescribe}
                            style={{
                                border: '1px solid #f59e0b', background: '#fff', color: '#b45309',
                                borderRadius: 10, padding: '8px 12px', fontWeight: 800, cursor: 'pointer',
                                opacity: !media?.url || aiBusy ? 0.55 : 1,
                            }}
                        >
                            {aiBusy === 'describe' ? '⏳ Describing…' : '✨ AI Describe (transcript)'}
                        </button>
                        <button
                            type="button"
                            disabled={!transcript.trim() || !!aiBusy}
                            onClick={handleMarkMistakes}
                            style={{
                                border: '1px solid #f59e0b', background: '#fff', color: '#b45309',
                                borderRadius: 10, padding: '8px 12px', fontWeight: 800, cursor: 'pointer',
                                opacity: !transcript.trim() || aiBusy ? 0.55 : 1,
                            }}
                        >
                            {aiBusy === 'mark' ? '⏳ Marking…' : '🩹 AI Mark mistakes [wrong/correct]'}
                        </button>
                    </div>
                    <div>
                        <div style={{ fontSize: '0.72rem', fontWeight: 800, color: '#a16207', marginBottom: 4 }}>Transcript (as spoken)</div>
                        <textarea
                            value={transcript}
                            onChange={(e) => {
                                setTranscript(e.target.value);
                                persistAnswers({ transcript: e.target.value });
                            }}
                            rows={4}
                            placeholder="Run AI Describe to fill…"
                            style={{
                                width: '100%', boxSizing: 'border-box', borderRadius: 10,
                                border: '1px solid #fcd34d', padding: 10, fontFamily: 'inherit', fontSize: '0.9rem',
                            }}
                        />
                    </div>
                    <div>
                        <div style={{ fontSize: '0.72rem', fontWeight: 800, color: '#a16207', marginBottom: 4 }}>Marked ([wrong/correct])</div>
                        <textarea
                            value={marked}
                            onChange={(e) => {
                                setMarked(e.target.value);
                                persistAnswers({ transcriptMarked: e.target.value });
                            }}
                            rows={4}
                            placeholder="Run AI Mark mistakes…"
                            style={{
                                width: '100%', boxSizing: 'border-box', borderRadius: 10,
                                border: '1px solid #fcd34d', padding: 10, fontFamily: 'inherit', fontSize: '0.9rem',
                            }}
                        />
                    </div>
                </div>
            )}
        </div>
    );
}
